import React from 'react';
import styled from '@emotion/styled';
import { theme } from 'common/theme';
import { LatanaText } from 'common/components/LatanaText';
import ModalV2 from 'common/components/Modal/ModalV2';
import SimpleDropdown from './SimpleDropdown';
import ColorPickerInput from './ColorPickerInput';
import { useTranslation } from 'react-i18next';

export const DEFAULT_BRAND_COLOR = '#000000';

const ModalContent = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  background-color: ${({ theme }) => theme.v2.colours.gray0};
  padding: ${({ theme }) => theme.v2.space(2, 2.5)};
  border-radius: ${({ theme }) => theme.v2.borderRadius * 2}px;
`;

const ModalHeader = styled.div`
  margin-bottom: ${theme.space(1)};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.space(1)};
`;

const AddBrandButton = styled.button`
  padding: ${theme.space(1.5, 3)};
  background-color: ${theme.v2.colours.latana};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  display: block;
  margin: 0 auto;

  &:hover {
    background-color: ${theme.v2.colours.latana900};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BrandDropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space(2)};
  margin-bottom: ${theme.space(2.5)};
`;

interface AddCompetitorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (competitor: string, color: string) => void;
  availableBrands: string[];
  defaultBrandColors: Record<string, string>;
}
const BrandDropdown: React.FC<{
  brandOptions: { value: string; label: string }[];
  selectedBrand: string | null;
  onChange: (value: string) => void;
  selectedColor: string;
  onColorChange: (color: string) => void;
}> = ({ brandOptions, selectedBrand, onChange, selectedColor, onColorChange }) => {
  const { t } = useTranslation();

  return (
    <BrandDropdownWrapper onClick={e => e.stopPropagation()}>
      <SimpleDropdown options={brandOptions} value={selectedBrand} onChange={onChange} placeholder={t('appearance.selectCompetitor')} />
      <ColorPickerInput color={selectedColor} onChange={onColorChange} useFixedPosition={true} />
    </BrandDropdownWrapper>
  );
};

const AddCompetitorModal: React.FC<AddCompetitorModalProps> = ({ isOpen, onClose, onAdd, availableBrands, defaultBrandColors }) => {
  const { t } = useTranslation();
  const [selectedCompetitor, setSelectedCompetitor] = React.useState<string | null>(null);
  const [selectedColor, setSelectedColor] = React.useState(DEFAULT_BRAND_COLOR);

  const brandOptions = React.useMemo(() => {
    return availableBrands.map(brandName => ({
      value: brandName,
      label: brandName,
    }));
  }, [availableBrands]);

  const handleSelect = React.useCallback(
    (value: string) => {
      setSelectedCompetitor(value);
      setSelectedColor(defaultBrandColors[value] || DEFAULT_BRAND_COLOR);
    },
    [defaultBrandColors],
  );

  const handleAdd = React.useCallback(() => {
    if (selectedCompetitor) {
      onAdd(selectedCompetitor, selectedColor);
      onClose();
      setSelectedCompetitor(null);
      setSelectedColor(DEFAULT_BRAND_COLOR);
    }
  }, [selectedCompetitor, selectedColor, onAdd, onClose]);

  return (
    <ModalV2 isOpen={isOpen} onClose={onClose} modalId='add-competitor-color-modal' title={t('appearance.addCompetitorModalTitle')}>
      <ModalContent>
        <ModalHeader>
          <LatanaText variant='B2' color='gray600'>
            {t('appearance.addCompetitorModalDescription')}
          </LatanaText>
        </ModalHeader>

        <FormGroup>
          <BrandDropdown
            brandOptions={brandOptions}
            selectedBrand={selectedCompetitor}
            onChange={handleSelect}
            selectedColor={selectedColor}
            onColorChange={setSelectedColor}
          />
        </FormGroup>

        <AddBrandButton onClick={handleAdd} disabled={!selectedCompetitor || !selectedColor}>
          {t('appearance.addBrand')}
        </AddBrandButton>
      </ModalContent>
    </ModalV2>
  );
};

export default React.memo(AddCompetitorModal);
